import "./index.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import {
	Window,
	WindowActionsBar,
} from "@tql/ui-components-react/kendo/dialogs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { settings_01Line } from "@tql/ui-components-react/untitled/icons";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";

import { debugModeOpenAtom } from "~/features/debug-mode/state/debug-mode-state.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import type { DebugLogEntry } from "~/shared/utils/debug-bus.ts";
import { debugBus } from "~/shared/utils/debug-bus.ts";

const handleCopy = (data: unknown) => {
	navigator.clipboard.writeText(JSON.stringify(data, null, 2));
};

export function DebugModeWidget() {
	const { isEnabled } = useFeatureFlagGate("traxLtlDebugMode");
	const [isOpen, setIsOpen] = useAtom(debugModeOpenAtom);
	const [logs, setLogs] = useState<DebugLogEntry[]>([]);
	const windowAppendTarget =
		typeof document === "undefined" ? undefined : document.body;

	useEffect(() => {
		if (!isEnabled) return;
		return debugBus.subscribe((log) => {
			setLogs((prev) => [log, ...prev].slice(0, 50));
		});
	}, [isEnabled]);

	const toggleModal = () => {
		setIsOpen(!isOpen);
	};

	const clearLogs = () => setLogs([]);

	if (!isEnabled) return null;

	return (
		<>
			<button
				className={"debug-mode-trigger"}
				onClick={toggleModal}
				title={"Open Debug API"}
				type={"button"}
			>
				<SvgIcon icon={settings_01Line} size={"xlarge"} />
			</button>

			{isOpen ? (
				<Window
					appendTo={windowAppendTarget}
					className={"debug-mode-window"}
					initialHeight={400}
					initialWidth={500}
					onClose={() => setIsOpen(false)}
					style={{ zIndex: 2_147_483_647 }}
					title={`API Inspector (${logs.length})`}
				>
					<div className={"debug-mode-layout"}>
						<div className={"debug-mode-content"}>
							{logs.length === 0 && (
								<div className={"debug-mode-empty-state"}>
									No API calls captured yet.
								</div>
							)}
							{logs.map((log) => {
								const isErrorResponse =
									log.type === "response" && (log.status ?? 0) >= 400;
								const entryClassName = `debug-log-entry${
									isErrorResponse ? " debug-log-entry-error" : ""
								}`;

								return (
									<div key={log.id} className={entryClassName}>
										<div className={"debug-log-header"}>
											<span className={`debug-log-type ${log.type}`}>
												{log.type.toUpperCase()}
											</span>
											<span className={"debug-log-timestamp"}>
												{log.timestamp.toLocaleTimeString()}
											</span>
										</div>
										<div className={"debug-log-url"}>
											{log.method} {log.url}
										</div>
										{log.status !== undefined && (
											<div
												className={`debug-log-status${
													isErrorResponse ? " debug-log-status-error" : ""
												}`}
											>
												Status: <strong>{log.status}</strong>
											</div>
										)}
										<div className={"debug-log-item-actions"}>
											<Button
												onClick={() => handleCopy(log)}
												variant={"tertiary"}
											>
												Copy JSON
											</Button>
										</div>
										<details className={"debug-log-details"}>
											<summary className={"debug-log-summary"}>
												View Details
											</summary>
											<pre className={"debug-log-pre"}>
												{JSON.stringify(log.body ?? log.headers, null, 2)}
											</pre>
										</details>
									</div>
								);
							})}
						</div>
					</div>
					<WindowActionsBar layout={"end"}>
						<Button onClick={clearLogs} variant={"secondary"}>
							Clear
						</Button>
					</WindowActionsBar>
				</Window>
			) : null}
		</>
	);
}
